import { useFormBuilderFactory } from "../hooks/use-form-builder-factory";
import type {
  MinimalFormField,
  PlaceholderFormField,
  PlaceholderFormFields,
  Resolver,
} from "../types/form-fields";

type FormFields = PlaceholderFormFields;
type FormField = PlaceholderFormField;

export const formFieldToDefaultValues = (
  fields: FormFields,
  resolvers: any
) => {  
  return Object.fromEntries(
    (fields as MinimalFormField[]).flat(Infinity).reduce((acc, field) => {
      let _defaultValue =
        field.defaultValue ?? resolvers[field.type]?.defaultValue;

      if (_defaultValue === undefined) {
        _defaultValue = resolvers.text.defaultValue;
      }

      if (typeof _defaultValue === "function") {
        _defaultValue = _defaultValue(field);
      }

      if (
        ["section", "stepper"].includes(field.type) &&
        field.name.startsWith("_")
      ) {
        Object.entries(_defaultValue).forEach(([key, value]) => {
          acc.push([key, value]);
        });
      } else {
        acc.push([field.name, _defaultValue]);
      }

      return acc;
    }, [])
  );
};

/* Recursive function to flatten FormFields into a Record<string, FormField> where the key is the field path */
export const flattenFormFields = (
  fields: FormFields,
  ignoreUnderscore = true,
  ignoreLoopable = false,
  namePrefix: string = "",
  values: any = {},
  resolvers: any
) => {
  let result: Record<string, FormField & { __resolver: Resolver<unknown> }> =
    {};

  (fields.flat(Infinity) as FormField[]).forEach((field: FormField) => {
    const fieldResolver = resolvers[field.type] ?? resolvers.text;

    const fieldName = (() => {
      if (!namePrefix) {
        return field.name;
      }

      if (ignoreUnderscore && namePrefix.startsWith("_")) {
        return field.name;
      }

      if (fieldResolver.hasSubFields) {
        if (field.name.startsWith("_")) {
          return namePrefix;
        }
      }

      if (/^\d+$/.test(field.name)) {
        return `${namePrefix}[${field.name}]`;
      }

      return `${namePrefix}.${field.name}`;
    })();

    if (field.shouldRender && !field.shouldRender(values)) {
      return;
    }

    if (!(ignoreUnderscore && fieldName.startsWith("_"))) {
      result[fieldName] = { ...field, __resolver: fieldResolver };
    }

    if (
      fieldResolver.hasSubFields &&
      (!ignoreLoopable || !fieldResolver.isLoopable)
    ) {
      result = {
        ...result,
        ...flattenFormFields(
          field.options?.fields ?? [],
          ignoreUnderscore,
          ignoreLoopable,
          fieldResolver.isLoopable ? `${fieldName}[0]` : fieldName,
          values,
          resolvers
        ),
      };
    }
  });

  return result;
};
